import { faCheckCircle, faFilter, faFloppyDisk, faSliders, faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ActionIcon, Group, Input, Modal, ScrollArea, Tabs, TextInput, Tooltip } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useSelectedAccountId } from '@vision/ui/hooks';
import { Nullable } from '@vision/ui/interfaces';
import { UserFilter } from '@vision/ui/interfaces/user-filter';
import {
  useCreateUserFilterMutation,
  useDeleteUserFilterMutation,
  useGetUserFiltersQuery,
  useUpdateUserFilterMutation,
} from '@vision/ui/services';
import { ensureArray } from '@vision/ui/utils';
import { FilterTitleInput } from './components';
import { ModalFooter } from '../ModalFooter';
import { Prompt } from '../Prompt';
import { showSuccessNotification } from '../VNotification';
import { TooltipCard } from '../TooltipCard';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classes from './FilterListModal.module.css';
import clsx from 'clsx';

enum ActiveTabList {
  FILTER = 'FILTER',
  SAVED_FILTERS = 'SAVED_FILTERS',
}

interface FilterListModalProps {
  filterKey?: string;
  filterData?: Record<string, any>;
  onApplyFilter?: (filter: UserFilter) => void;
  classNames: {
    button: string;
  };
}

export function FilterListModal({ filterData, filterKey, onApplyFilter, classNames }: FilterListModalProps) {
  const { t } = useTranslation();
  const [filterModalOpened, setFilterModalOpen] = useDisclosure(false);
  const accountId = useSelectedAccountId();
  const [opened, handlers] = useDisclosure();
  const [selectedDeleteFilterId, setSelectedDeleteFilterId] = useState<Nullable<string>>(null);

  const { data: userFilters, refetch: refetchUserFilters } = useGetUserFiltersQuery({
    accountId: accountId,
    key: filterKey,
  });

  const [createUserFilter, { isLoading: isCreatingUserFilter }] = useCreateUserFilterMutation();
  const [deleteUserFilter, { isLoading: isDeletingUserFilter }] = useDeleteUserFilterMutation();
  const [updateUserFilter, { isLoading: isUpdatingUserFilter }] = useUpdateUserFilterMutation();

  const [activeTab, setActiveTab] = useState<ActiveTabList>(ActiveTabList.FILTER);

  const [newFilterName, setNewFilterName] = useState('');
  const [selectedFilter, setSelectedFilter] = useState<Nullable<UserFilter>>(null);

  useEffect(() => {
    if (userFilters?.data?.user_filters?.length > 0) {
      setActiveTab(ActiveTabList.SAVED_FILTERS);
    }
  }, [userFilters?.data?.user_filters?.length]);

  const handleSaveFilter = () => {
    if (!filterKey || !newFilterName || !filterData) {
      return;
    }

    const filterDataJson = JSON.stringify(filterData);

    createUserFilter({
      accountId: accountId,
      name: newFilterName,
      key: filterKey,
      filter: filterDataJson,
    })
      .unwrap()
      .then(() => {
        refetchUserFilters();
        setNewFilterName('');
        handleOnClose();
      });
  };

  const handleApplyFilter = () => {
    onApplyFilter?.(selectedFilter);
    handleOnClose();
    showSuccessNotification({
      title: t('success'),
      message: t('filterAppliedSuccessfully'),
    });
  };

  const handleOnClose = () => {
    setSelectedFilter(null);
    setNewFilterName('');
    setFilterModalOpen.close();
  };

  const openFilterModal = () => {
    setFilterModalOpen.open();
  };

  const handleOnUpdateFilterName = (filterId: string, name: string, selectedUserFilter: UserFilter) => {
    updateUserFilter({
      accountId: accountId,
      filterId: filterId,
      data: { ...selectedUserFilter, name: name },
    })
      .unwrap()
      .then(() => {
        refetchUserFilters();
      });
  };

  const handleSelectDeleteFilter = (filterId: string) => {
    setSelectedDeleteFilterId(filterId);
    handlers.open();
  };

  const handleDeleteFilterConfirm = () => {
    deleteUserFilter({ accountId: accountId, filterId: selectedDeleteFilterId })
      .unwrap()
      .then(() => {
        refetchUserFilters();
        setSelectedDeleteFilterId(null);
        handlers.close();
        if (selectedFilter?.id === selectedDeleteFilterId) {
          setSelectedFilter(null);
        }
      });
  };

  const loading = isCreatingUserFilter || isDeletingUserFilter || isUpdatingUserFilter;

  return (
    <>
      <TooltipCard position="right" zIndex={1} openDelay={200}>
        <TooltipCard.Target>
          <Tooltip label={t('savedFilters')} position="bottom">
            <ActionIcon
              className={clsx(classNames.button)}
              data-testid="button-saved-filters-modal"
              onClick={openFilterModal}
            >
              <FontAwesomeIcon icon={faSliders} size="sm" />
            </ActionIcon>
          </Tooltip>
        </TooltipCard.Target>
        <TooltipCard.Dropdown size="sm" variant="light">
          {t('filterButtonDescription')}
        </TooltipCard.Dropdown>
      </TooltipCard>
      <Modal
        opened={filterModalOpened}
        onClose={handleOnClose}
        size={450}
        data-testid="filter-list-modal"
        title={t('filters')}
        keepMounted={true}
      >
        <Tabs value={activeTab} onChange={(value) => setActiveTab(value as ActiveTabList)} pb={30}>
          <Tabs.List px={30}>
            <Tabs.Tab value={ActiveTabList.FILTER} leftSection={<FontAwesomeIcon icon={faFilter} />}>
              {t('filter')}
            </Tabs.Tab>
            <Tabs.Tab value={ActiveTabList.SAVED_FILTERS} leftSection={<FontAwesomeIcon icon={faFloppyDisk} />}>
              {t('savedFilters')}
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value={ActiveTabList.FILTER} pt={10} px={30} h={200}>
            <TextInput
              data-testid="input-new-filter-name"
              label={t('filterName')}
              value={newFilterName}
              onChange={(e) => setNewFilterName(e.target.value)}
              disabled={isCreatingUserFilter}
            />
            <Input.Description pt={10} className={classes.filterDescription}>
              {t('filterDescription')}
            </Input.Description>
          </Tabs.Panel>

          <Tabs.Panel value={ActiveTabList.SAVED_FILTERS} pt={10} px={30} h={200}>
            <ScrollArea h={200} scrollbarSize={8} offsetScrollbars={true}>
              {ensureArray(userFilters?.data?.user_filters).map((filter) => (
                <Group
                  key={filter.id}
                  w="100%"
                  justify="space-between"
                  align="center"
                  h={48}
                  className={clsx(classes.filterItem, selectedFilter?.id === filter.id && classes.selectedFilter)}
                >
                  <Group gap={10} align="center">
                    <FontAwesomeIcon icon={faCheckCircle} onClick={() => setSelectedFilter(filter)} />

                    <FilterTitleInput
                      title={filter.name}
                      handleOnUpdateFilterName={(name) => handleOnUpdateFilterName(filter.id, name, filter)}
                      loading={loading}
                      className={classes.penIcon}
                    />
                  </Group>
                  <FontAwesomeIcon
                    icon={faTrashAlt}
                    className={classes.trashIcon}
                    onClick={() => handleSelectDeleteFilter(filter.id)}
                    style={{ marginRight: 10 }}
                  />
                </Group>
              ))}
            </ScrollArea>
          </Tabs.Panel>
        </Tabs>
        <ModalFooter
          onCancel={handleOnClose}
          saveBtnProps={{
            disabled:
              (activeTab === ActiveTabList.FILTER && !newFilterName) ||
              (activeTab === ActiveTabList.SAVED_FILTERS && !selectedFilter),
            loading: loading,
          }}
          onSave={activeTab === ActiveTabList.FILTER ? handleSaveFilter : handleApplyFilter}
          saveBtnLabel={activeTab === ActiveTabList.FILTER ? t('save') : t('apply')}
        />
      </Modal>

      <Prompt
        heading={t('delete')}
        content={t('prompts.delete')}
        onConfirm={handleDeleteFilterConfirm}
        saveBtnProps={{
          color: 'red.4',
        }}
        onCancel={handlers.close}
        opened={opened}
      />
    </>
  );
}
